import React, { useEffect, useState } from 'react';
import { useAuth } from '../components/AuthContext'; 
import { useNavigate } from 'react-router-dom'; 
import { messageErreurApi } from '../api/client';

// COMPOSANTS MODULAIRES
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';

// FONCTION UTILITAIRE POUR LES ICÔNES
const getStatIcon = (title: string): React.ReactElement => {
    switch (title) {
        case 'Effectif Total':
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
        case 'Fins de Contrat (Mois)':
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.398 16c-.77 1.333.192 3 1.732 3z" /></svg>;
        case 'Nouveaux Employés':
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7zM19 11a2 2 0 110 4 2 2 0 010-4zM21 11h-2" /></svg>;
        default:
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    }
};

// Lignes de la carte "Effectifs par Type" : la clé sert de filtre dans l'URL
const CONTRACT_ROWS = [
    { key: 'CDI', label: 'CDI', icon: '💼', border: 'border-l-primary', color: 'text-primary' },
    { key: 'CDD', label: 'CDD', icon: '⏳', border: 'border-l-info', color: 'text-info' },
    { key: 'STAGE', label: 'Stage', icon: '🎓', border: 'border-l-warning', color: 'text-warning' },
];

// Chaque ligne ouvre la liste des employés filtrée sur ce type de contrat
const ContractCountCard: React.FC<{
    counts: Record<string, number>;
    onSelect: (contractType: string) => void;
}> = ({ counts, onSelect }) => (
    <div className="card bg-base-100 shadow-md col-span-1 md:col-span-1">
        <div className="card-body p-4 sm:p-6">
            <h3 className="card-title text-base sm:text-lg font-bold">Effectifs par Type</h3>
            <p className="text-xs sm:text-sm text-base-content/70 mb-3 hidden sm:block">Cliquez sur une ligne pour voir la liste</p>

            <div className="flex flex-col space-y-2 sm:space-y-3">
                {CONTRACT_ROWS.map(row => (
                    <button
                        key={row.key}
                        type="button"
                        onClick={() => onSelect(row.key)}
                        title={`Voir les employés en ${row.label}`}
                        className={`flex justify-between items-center w-full text-left p-2 sm:p-3 rounded-lg bg-base-200/50 hover:bg-base-200 hover:shadow-md transition duration-150 border-l-4 ${row.border}`}
                    >
                        <div className="flex items-center space-x-2 sm:space-x-3">
                            <span className={`text-lg sm:text-xl font-bold ${row.color}`}>{row.icon}</span>
                            <span className="text-sm sm:text-base font-semibold">{row.label}</span>
                        </div>
                        <span className="text-xl sm:text-2xl font-extrabold text-base-content">
                            {counts[row.key] || 0}
                            <span className="text-base opacity-40 ml-1">›</span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    </div>
);

// Formatage FCFA (Intl natif, pas de dépendance)
const formatFcfa = (amount: number): string =>
    `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(amount || 0)} FCFA`;

// Date ISO -> "14/08/2026"
const formatShortDate = (isoDate: string): string => {
    if (!isoDate) return '—';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};



const Dashboard: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // État pour contrôler l'ouverture/fermeture du panneau de notifications
    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
    
    // Données dynamiques récupérées du backend
    const [stats, setStats] = useState({
        total_employees: 0,
        on_leave_today: 0,
        contracts_ending_soon: 0,
        new_employees_this_month: 0,
        contracts_by_type: { CDI: 0, CDD: 0, STAGE: 0 }
    });
    const [loadingStats, setLoadingStats] = useState(true);
    const [statsError, setStatsError] = useState<string | null>(null);

    // Stats détaillées pour les cartes de direction (endpoint existant /stats/)
    const [empStats, setEmpStats] = useState({
        active_employees: 0,
        total_salary_mass: 0,
        by_company: [] as { company__name: string | null; count: number }[],
    });
    const [expiredCount, setExpiredCount] = useState(0);
    // Bulletins générés sur les 30 derniers jours (historique des imports paie)
    const [recentPayslips, setRecentPayslips] = useState({ count: 0, batches: 0, lastDate: '' });

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const mod = await import('../api/stats');
                const data = await mod.fetchDashboardStats();
                if (!mounted) return;
                setStats({
                    total_employees: data.total_employees || 0,
                    on_leave_today: data.on_leave_today || 0,
                    contracts_ending_soon: data.contracts_ending_soon || 0,
                    new_employees_this_month: data.new_employees_this_month || 0,
                    contracts_by_type: data.contracts_by_type || { CDI: 0, CDD: 0, STAGE: 0 }
                });

                // Cartes complémentaires : chargées en parallèle, chacune tolérante
                // à l'échec pour ne jamais bloquer l'affichage du tableau de bord.
                const payrollMod = await import('../api/payroll');
                const [detailed, expired, imports] = await Promise.all([
                    mod.fetchEmployeeStats(),
                    mod.fetchExpiredContracts().catch(() => []),
                    payrollMod.fetchPayrollImports().catch(() => []),
                ]);
                if (!mounted) return;
                setEmpStats({
                    active_employees: detailed.active_employees,
                    total_salary_mass: detailed.total_salary_mass,
                    by_company: detailed.by_company,
                });
                setExpiredCount(expired.length);

                // Bulletins générés sur les 30 derniers jours
                const since = Date.now() - 30 * 24 * 60 * 60 * 1000;
                const recent = imports.filter(i => new Date(i.created_at).getTime() >= since);
                setRecentPayslips({
                    count: recent.reduce((sum, i) => sum + (i.bulletin_count || 0), 0),
                    batches: recent.length,
                    lastDate: imports[0]?.created_at || '',
                });
            } catch (e: any) {
                if (!mounted) return;
                setStatsError(messageErreurApi(e, 'Erreur de chargement des statistiques'));
            } finally {
                if (mounted) setLoadingStats(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Les détails des contrats en fin ne sont pas affichés ici; navigation vers la page dédiée
    
    // Fonction pour naviguer vers la page FinContrat
    const handleViewContracts = () => {
        navigate('/fin-contrat');
    };

    // Fonction pour naviguer vers la page Domiciliation
    const handleViewDomiciliation = () => {
        navigate('/domiciliation');
    };

    // Ouvre la liste des employés filtrée sur un type de contrat (CDI / CDD / STAGE)
    const handleViewByContract = (contractType: string) => {
        navigate(`/employees?contrat=${contractType}`);
    };

    // Protection de la route
    if (!user) {
        navigate('/login', { replace: true });
        return null;
    }
    
    // Préparation des données pour les statistiques
    type KeyStat = {
        title: string;
        value: number;
        desc?: string;
        descColor?: string;
        figureColor: string;
        actionLabel: string;
        actionClass: string;
        onAction: () => void;
    };

    const keyStatsData: KeyStat[] = [
        {
            title: 'Effectif Total',
            value: stats.total_employees,
            figureColor: 'text-primary',
            actionLabel: 'Voir la liste',
            actionClass: 'btn-primary',
            onAction: () => navigate('/employees'),
        },
        {
            title: 'Fins de Contrat (Mois)',
            value: stats.contracts_ending_soon,
            descColor: 'text-warning',
            figureColor: 'text-warning',
            actionLabel: 'Voir contrats',
            actionClass: 'btn-warning',
            onAction: handleViewContracts,
        },
        {
            title: 'Nouveaux Employés',
            value: stats.new_employees_this_month,
            desc: 'Ce mois-ci',
            descColor: 'text-success',
            figureColor: 'text-success',
            actionLabel: 'Voir la liste',
            actionClass: 'btn-success',
            onAction: () => navigate('/employees?recents=1'),
        },
    ];
    
    return (
        <div className={`flex min-h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
            
            {/* -------------------- Sidebar (Composant Modulaire) -------------------- */}
            <Sidebar />

            {/* -------------------- Contenu Principal -------------------- */}
            <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-y-auto">

                {/* Navbar Supérieure (Composant Modulaire) */}
                <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />

                {/* Contenu principal du tableau de bord (grille) */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
                    
                    {/* Rangée de Statistiques Clés */}
                    <div className="lg:col-span-4">
                        <div className="stats stats-vertical sm:stats-horizontal shadow-xl w-full bg-base-100 mb-4 md:mb-6 border border-base-300">
                            {loadingStats && (
                                <div className="w-full flex items-center justify-center py-8">
                                    <span className="loading loading-spinner loading-lg text-warning"></span>
                                </div>
                            )}
                            {!loadingStats && statsError && (
                                <div className="w-full p-6 text-center">
                                    <div className="text-error font-semibold">{statsError}</div>
                                    <div className="text-xs text-base-content/60 mt-1">
                                        Les chiffres ne sont pas affichés tant que le chargement a échoué —
                                        aucune donnée n'est perdue.
                                    </div>
                                    <button
                                        className="btn btn-sm btn-warning mt-3"
                                        onClick={() => window.location.reload()}
                                    >
                                        Réessayer
                                    </button>
                                </div>
                            )}
                            {!loadingStats && !statsError && keyStatsData.map((stat, index: number) => (
                                <div
                                    key={index}
                                    className="stat transform cursor-pointer hover:bg-base-200/60 transition duration-150"
                                    onClick={stat.onAction}
                                    title={stat.actionLabel}
                                >
                                    <div className={`stat-figure ${stat.figureColor} hidden sm:block`}>{getStatIcon(stat.title)}</div>
                                    <div className="stat-title font-medium text-sm sm:text-base">{stat.title}</div>
                                    <div className={`stat-value text-2xl sm:text-3xl md:text-4xl ${stat.descColor || ''}`}>{stat.value}</div>
                                    {stat.desc && <div className={`stat-desc ${stat.descColor || ''}`}>{stat.desc}</div>}
                                    <div className="stat-actions mt-2 flex gap-2">
                                        <button
                                            className={`btn btn-sm ${stat.actionClass}`}
                                            onClick={(e) => { e.stopPropagation(); stat.onAction(); }}
                                        >
                                            {stat.actionLabel}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Cartes masquées si le chargement a échoué : elles
                        afficheraient des zéros trompeurs. */}
                    {!statsError && (<>

                    {/* Rangée Contrats et Domiciliation */}
                    <div className="col-span-1 lg:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        
                        <ContractCountCard
                            counts={stats.contracts_by_type}
                            onSelect={handleViewByContract}
                        />

                        {/* Carte Domiciliation */}
                        <div className="card bg-base-100 shadow-md col-span-1 md:col-span-1">
                            <div className="card-body p-4 sm:p-6">
                                <h3 className="card-title text-base sm:text-lg font-bold text-warning">💼 Domiciliation de Salaire</h3>
                                <p className="text-xs sm:text-sm text-base-content/70 mb-3">Gestion des attestations de domiciliation bancaire</p>
                                
                                <div className="flex flex-col space-y-4 flex-grow justify-center">
                                    <div className="p-4 rounded-lg bg-warning/10 border-2 border-warning/30 text-center">
                                        <div className="text-4xl mb-2">🏦</div>
                                        <p className="text-sm text-base-content/70">Consultez les attestations générées pour vos employés</p>
                                    </div>
                                </div>
                                
                                <div className="card-actions justify-end mt-4">
                                    <button
                                        className="btn btn-warning btn-block"
                                        onClick={handleViewDomiciliation}
                                    >
                                        Voir les domiciliations
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* -------------------- Indicateurs de pilotage -------------------- */}
                    <div className="col-span-1 lg:col-span-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">

                        {/* Masse salariale */}
                        <div className="card bg-base-100 shadow-md">
                            <div className="card-body p-4 sm:p-6">
                                <h3 className="card-title text-base sm:text-lg font-bold text-success">💰 Masse salariale</h3>
                                <p className="grow-0 text-xs text-base-content/60">Total des salaires de base, par mois</p>
                                <p className="text-2xl sm:text-3xl font-extrabold mt-2 break-words">
                                    {formatFcfa(empStats.total_salary_mass)}
                                </p>
                                <div className="grow-0 text-xs text-base-content/60 mt-2 border-t border-base-300 pt-2">
                                    Moyenne :{' '}
                                    <span className="font-semibold">
                                        {formatFcfa(empStats.active_employees ? empStats.total_salary_mass / empStats.active_employees : 0)}
                                    </span>{' '}
                                    sur {empStats.active_employees} employé{empStats.active_employees > 1 ? 's' : ''} actif{empStats.active_employees > 1 ? 's' : ''}
                                </div>
                            </div>
                        </div>

                        {/* Répartition par société */}
                        <div className="card bg-base-100 shadow-md">
                            <div className="card-body p-4 sm:p-6">
                                <h3 className="card-title text-base sm:text-lg font-bold">🏢 Par société</h3>
                                <p className="grow-0 text-xs text-base-content/60 mb-2">Cliquez pour filtrer la liste</p>
                                {empStats.by_company.length === 0 ? (
                                    <p className="text-sm text-base-content/50 py-4">Aucune donnée</p>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        {empStats.by_company.slice(0, 5).map((row, i) => {
                                            const name = row.company__name || 'Sans société';
                                            const top = empStats.by_company[0]?.count || 1;
                                            return (
                                                <button
                                                    key={`${name}-${i}`}
                                                    type="button"
                                                    disabled={!row.company__name}
                                                    onClick={() => navigate(`/employees?societe=${encodeURIComponent(name)}`)}
                                                    className="text-left rounded p-1.5 hover:bg-base-200 disabled:hover:bg-transparent disabled:opacity-60 transition"
                                                >
                                                    <div className="flex justify-between items-baseline text-sm">
                                                        <span className="truncate pr-2">{name}</span>
                                                        <span className="font-bold">{row.count}</span>
                                                    </div>
                                                    <progress
                                                        className="progress progress-primary h-1.5 w-full"
                                                        value={Math.round((row.count / top) * 100)}
                                                        max={100}
                                                    ></progress>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Nombre d'employés par état de contrat */}
                        <div className="card bg-base-100 shadow-md">
                            <div className="card-body p-4 sm:p-6">
                                <h3 className="card-title text-base sm:text-lg font-bold">👥 Nombre d'employés</h3>
                                <p className="grow-0 text-xs text-base-content/60 mb-2">Selon l'état du contrat — cliquez pour voir la liste</p>
                                <div className="grid grid-cols-2 gap-3 mt-1">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/employees?statut=ACTIF')}
                                        className="rounded-lg bg-success/10 hover:bg-success/20 p-4 text-center transition"
                                    >
                                        <div className="text-3xl font-extrabold text-success">
                                            {Math.max(stats.total_employees - expiredCount, 0)}
                                        </div>
                                        <div className="text-xs text-base-content/70 mt-1">Contrat en cours</div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/employees?statut=INACTIF')}
                                        className="rounded-lg bg-error/10 hover:bg-error/20 p-4 text-center transition"
                                    >
                                        <div className="text-3xl font-extrabold text-error">{expiredCount}</div>
                                        <div className="text-xs text-base-content/70 mt-1">Contrat terminé</div>
                                    </button>
                                </div>
                                <div className="grow-0 text-xs text-base-content/60 mt-3 border-t border-base-300 pt-2">
                                    {stats.total_employees} employé{stats.total_employees > 1 ? 's' : ''} au total
                                </div>
                            </div>
                        </div>

                        {/* Bulletins générés récemment */}
                        <div className="card bg-base-100 shadow-md">
                            <div className="card-body p-4 sm:p-6">
                                <h3 className="card-title text-base sm:text-lg font-bold">🧾 Bulletins générés</h3>
                                <p className="grow-0 text-xs text-base-content/60">Sur les 30 derniers jours</p>
                                <p className="text-4xl font-extrabold mt-2">{recentPayslips.count}</p>
                                <p className="grow-0 text-xs text-base-content/60 mt-1">
                                    {recentPayslips.batches === 0
                                        ? 'Aucune génération récente.'
                                        : `${recentPayslips.batches} génération${recentPayslips.batches > 1 ? 's' : ''} — dernière le ${formatShortDate(recentPayslips.lastDate)}`}
                                </p>
                                <div className="card-actions justify-end mt-3">
                                    <button className="btn btn-neutral btn-sm btn-block" onClick={() => navigate('/bulletin-de-paie')}>
                                        Générer les bulletins
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Contrats expirés */}
                        <div className="card bg-base-100 shadow-md">
                            <div className="card-body p-4 sm:p-6">
                                <h3 className="card-title text-base sm:text-lg font-bold text-error">⚠️ Contrats expirés</h3>
                                <p className="grow-0 text-xs text-base-content/60">Date de fin déjà dépassée</p>
                                <p className="text-4xl font-extrabold text-error mt-2">{expiredCount}</p>
                                <p className="grow-0 text-xs text-base-content/60 mt-1">
                                    {expiredCount === 0
                                        ? 'Rien à régulariser.'
                                        : 'À régulariser (renouvellement ou solde de tout compte).'}
                                </p>
                                <div className="card-actions justify-end mt-3">
                                    <button className="btn btn-error btn-sm btn-block" onClick={() => navigate('/contrats-expires')}>
                                        Voir les contrats expirés
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>

                    </>)}
                </div>
            </main>
            
            {/* -------------------- Panneau de Notifications (Composant Modulaire) -------------------- */}
            <NotificationPanel 
                isOpen={isNotifPanelOpen} 
                onClose={() => setIsNotifPanelOpen(false)} 
            />
        </div>
    );
};

export default Dashboard;